import jwt_decode from 'jwt-decode';
import { formatNotificationMessage } from '../components/User/Password/password-utils';
import {
  checkPasswordChangeLinkPath,
  checkPasswordCreateLinkPath,
  loginPath,
  passwordChangeFromLinkPath,
  passwordChangePath,
  passwordCreateFromLinkPath,
  register2faPath,
  sendPasswordChangeLinkPath,
  verify2faPath,
} from '../constants/apiEndpoints';
import { locationPath } from '../constants/app-routes';
import { localStorageConstants, notificationType } from '../constants/globals';
import { securityActions } from '../reducers/security';
import { createNotification } from '../utils';
import { decodeAndSetActions, errorAction } from '../utils/actions';
import apiService from '../utils/apiService';
import setJWTToken, { set2FAToken } from '../utils/security/setJWTToken';

export const login =
  (
    loginRequest,
    show2faVerificationModal,
    modalUserChangePasswordShowHandler,
    messages,
    history
  ) =>
  (dispatch) => {
    dispatch(securityActions.actionStart());
    return apiService
      .post(loginPath(), loginRequest)
      .then((response) => {
        const responseData = response?.data;
        const shouldChangePassword = responseData?.shouldChangePassword;
        localStorage.setItem(
          localStorageConstants.SHOULD_CHANGE_PASSWORD,
          shouldChangePassword
        );
        if (shouldChangePassword) {
          modalUserChangePasswordShowHandler();
          return;
        }
        const decodedToken = setTokensAndDecodeToken(responseData);
        decodeAndSetActions(decodedToken);
        dispatch(securityActions.setCurrentUser(decodedToken));
        localStorage.setItem(localStorageConstants.HISTORY_POP_UP, true);
        const google2fa = responseData?.google2fa;
        const qrCode = responseData?.google2faSecret;
        localStorage.setItem(localStorageConstants.GOOGLE_2FA_TOKEN, google2fa);
        localStorage.setItem(localStorageConstants.QR_CODE, qrCode);
        if (decodedToken?.google2fa) {
          show2faVerificationModal();
        } else {
          dispatch(deleteErrorSecurity());
          history.replace(locationPath.DASHBOARD);
        }
      })
      .catch((err) => {
        errorAction(err, securityActions.actionError, dispatch, messages);
      });
  };

export const setTokensAndDecodeToken = (res) => {
  const { token, refreshToken, allowedActions } = res || {};
  const decoded = jwt_decode(token);
  localStorage.setItem(localStorageConstants.JWT_TOKEN, token);
  localStorage.setItem(localStorageConstants.REFRESH_TOKEN, refreshToken);
  localStorage.setItem(
    localStorageConstants.USER_ROLE_REGEX,
    decoded?.role?.regexDescription
  );
  localStorage.setItem(localStorageConstants.ALLOWED_ACTIONS, allowedActions);
  setJWTToken(token);
  return decoded;
};

export const logout = (history) => (dispatch) => {
  dispatch(securityActions.actionStart());
  try {
    clearHeadersAndTokens();
    dispatch(securityActions.clearCurrentUser());
    dispatch(securityActions.verify2faSecretError({}));
    history.replace('/');
  } catch (err) {
    dispatch(securityActions.actionError(err));
  }
};

export const verify2fa = (verifyCode, userId, messages, history) => {
  return (dispatch) => {
    dispatch(securityActions.actionStart());
    return apiService
      .post(verify2faPath(userId), verifyCode)
      .then((response) => {
        verify2faSuccessfully(response, history);
        dispatch(securityActions.verify2faSecret());
      })
      .catch((err) => {
        localStorage.removeItem(localStorageConstants.GOOGLE_2FA_TOKEN);
        dispatch(securityActions.verify2faSecretError(err?.response?.data));
        errorAction(err, securityActions.actionError, dispatch, messages);
      });
  };
};

export const passwordChange = (userPasswordChangingDTO, callback, messages) => {
  return (dispatch) => {
    dispatch(securityActions.actionStart());
    return apiService
      .put(passwordChangePath(), userPasswordChangingDTO)
      .then((response) => {
        dispatch(securityActions.changePasswordByUser());
        createNotification(
          notificationType.success,
          messages?.passwordChange,
          response?.data
        );
        callback?.();
      })
      .catch((err) => {
        passwordErrorNotificationHandler(err, messages, dispatch);
      });
  };
};

export const register2fa = (id) => {
  return (dispatch) => {
    dispatch(securityActions.actionStart());
    return apiService
      .get(register2faPath(id))
      .then((response) => {
        dispatch(securityActions.register2fa(response.data));
      })
      .catch((err) => {
        dispatch(securityActions.actionError(err?.response?.data));
      });
  };
};

export const clearHeadersAndTokens = () => {
  setJWTToken(false);
  set2FAToken(false);
  localStorage.clear();
};

export const deleteErrorSecurity = () => {
  return (dispatch) => {
    dispatch(securityActions.clearError());
  };
};

export const sendPasswordChangeLink = (email, messages, callback) => {
  return (dispatch) => {
    dispatch(securityActions.actionStart());
    return apiService
      .post(sendPasswordChangeLinkPath(email))
      .then(() => {
        dispatch(securityActions.stopLoading());
        callback?.();
        messages &&
          createNotification(
            notificationType.success,
            messages?.title,
            messages?.successSubmit
          );
      })
      .catch((err) => {
        errorAction(err, securityActions.actionError, dispatch, messages);
      });
  };
};

export const checkPasswordChangeLink = (uuid, history) => {
  return (dispatch) => {
    dispatch(securityActions.actionStart());
    return apiService
      .get(checkPasswordChangeLinkPath(uuid))
      .catch(() => {
        history.push(locationPath.PAGE_NOT_FOUND);
      })
      .finally(() => {
        dispatch(securityActions.stopLoading());
      });
  };
};

export const checkPasswordCreateLink = (uuid, history) => {
  return (dispatch) => {
    dispatch(securityActions.actionStart());
    return apiService
      .get(checkPasswordCreateLinkPath(uuid))
      .catch(() => {
        history.push(locationPath.PAGE_NOT_FOUND);
      })
      .finally(() => {
        dispatch(securityActions.stopLoading());
      });
  };
};

export const passwordChangeFromLink = (
  uuid,
  userPasswordChangingDTO,
  callback,
  messages
) => {
  return (dispatch) => {
    dispatch(securityActions.actionStart());
    return apiService
      .put(passwordChangeFromLinkPath(uuid), userPasswordChangingDTO)
      .then((response) => {
        dispatch(securityActions.changePasswordByUser());
        callback?.();
        return response;
      })
      .then((response) => {
        createNotification(
          notificationType.success,
          messages?.passwordChange,
          response?.data
        );
      })
      .catch((err) => {
        passwordErrorNotificationHandler(err, messages, dispatch);
      });
  };
};

export const passwordCreateFromLink = (
  uuid,
  userPasswordChangingDTO,
  callback,
  messages
) => {
  return (dispatch) => {
    dispatch(securityActions.actionStart());
    return apiService
      .put(passwordCreateFromLinkPath(uuid), userPasswordChangingDTO)
      .then((response) => {
        dispatch(securityActions.changePasswordByUser());
        callback?.();
        return response;
      })
      .then((response) => {
        createNotification(
          notificationType.success,
          messages?.passwordChange,
          response?.data
        );
      })
      .catch((err) => {
        passwordErrorNotificationHandler(err, messages, dispatch);
      });
  };
};

function verify2faSuccessfully(response, history) {
  const google2faToken = response.headers['x-2fa-token-update'];
  localStorage.setItem(localStorageConstants.GOOGLE_2FA_TOKEN, google2faToken);
  set2FAToken(google2faToken);
  history.replace(locationPath.DASHBOARD);
}

const passwordErrorNotificationHandler = (err, messages, dispatch) => {
  const specialSymbol = '\u2022';
  const errMessage = err?.response?.data?.message;
  const shouldStyleMessage = errMessage?.includes(specialSymbol);
  let errObject = err;
  if (shouldStyleMessage) {
    const message = formatNotificationMessage(errMessage, specialSymbol);
    errObject = {
      ...err,
      response: {
        ...err?.response,
        data: { ...err?.response?.data, message },
      },
    };
  }
  errorAction(errObject, securityActions.actionError, dispatch, messages, 8000);
};
